import { create } from 'zustand';
import { apiRequest, ApiError } from './api';

export type SessionUser = {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  job_title: string | null;
  profile_photo_url: string | null;
  address: string | null;
  is_active: boolean;
  last_login_at: string | null;
  roles: string[];
};

type AuthState = {
  user: SessionUser | null;
  /** true once a /auth/me probe has completed (regardless of outcome) */
  initialized: boolean;
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Replace the current session user (used after self-service profile edits). */
  setUser: (user: SessionUser) => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  initialized: false,

  async initialize() {
    try {
      const { user } = await apiRequest<{ user: SessionUser }>('/auth/me');
      set({ user, initialized: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        set({ user: null, initialized: true });
        return;
      }
      set({ user: null, initialized: true });
    }
  },

  async login(email, password) {
    const { user } = await apiRequest<{ user: SessionUser }>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    set({ user });
  },

  async logout() {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } finally {
      set({ user: null });
    }
  },

  setUser(user) {
    set({ user });
  },
}));
